import React from 'react';
import { useTranslation } from 'react-i18next';

import { Tab, TabLabel, Thumb, Track } from './ComposerRailTabs.styles';

/** Which rail of an ad composer a phone shows: the setup (Ingredients) or what it makes. */
export type ComposerRailTab = 'ingredients' | 'output';

/** The side a rail enters from on a tab switch: what it makes from the right, the Ingredients from the left. */
export type RailEnter = 'left' | 'right';

export interface ComposerRailTabsProps {
  value: ComposerRailTab;
  onChange: (tab: ComposerRailTab) => void;
  /** The output rail's name in this composer: "Video", "Banners" or "Mini-games" (an i18n key). */
  outputLabel: string;
  /** Show the strip at every width, not only on a phone: what the composer makes does not fit beside the rail (pair
   *  with the split's `$tabbed`). */
  always?: boolean;
}

/**
 * The side the shown rail entered from, for the split's entrance (`$railEnter`): undefined until the owner switches
 * tabs, so the rail never slides in as the sheet opens (the sheet has its own entrance), only on a switch. `picked`
 * says whether the tab is the owner's pick: a tab that still follows the ad (the video composer's default, Video once
 * its storyboard loads) changes silently. Follows the tab during render (React's "adjust state when a prop changes"),
 * so the entrance starts with the switch's frame.
 */
export const useRailEnter = (tab: ComposerRailTab, picked = true): RailEnter | undefined => {
  const [seen, setSeen] = React.useState(tab);
  const [enter, setEnter] = React.useState<RailEnter | undefined>(undefined);
  if (tab !== seen) {
    setSeen(tab);
    if (picked) setEnter(tab === 'output' ? 'right' : 'left');
  }
  return enter;
};

/**
 * On a phone, an ad composer's two rails (its Ingredients and what it makes) are tabs instead of one long column:
 * this strip picks the one shown, its active pill gliding to the pick. Hidden on wider screens. The composer keeps
 * both rails mounted and passes the pick to its split (`$mobileTab`), so a tab switch keeps what was typed and what
 * is running.
 */
export const ComposerRailTabs = ({
  value,
  onChange,
  outputLabel,
  always,
}: ComposerRailTabsProps): React.ReactElement => {
  const { t } = useTranslation();
  const tabs: { id: ComposerRailTab; label: string }[] = [
    { id: 'ingredients', label: 'Ingredients' },
    { id: 'output', label: outputLabel },
  ];
  return (
    <Track role="tablist" aria-label={t('Composer views')} $always={always}>
      <Thumb aria-hidden="true" $second={value === 'output'} />
      {tabs.map((tab) => (
        <Tab
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={value === tab.id}
          $active={value === tab.id}
          onClick={() => onChange(tab.id)}
        >
          <TabLabel text={tab.label} />
        </Tab>
      ))}
    </Track>
  );
};

export default ComposerRailTabs;
